import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * MobileTrittico — video with every trick to force iOS Safari autoplay
 * and hide any embedded-video chrome (play button, controls, picker, PiP).
 * If autoplay still fails after 2s, gracefully fades to the static WebP.
 */
function MobileTrittico() {
  return (
    <img
      className="tri-illustration__mobile-video"
      src={process.env.PUBLIC_URL + '/illustrations/anima-tree.mobile.gif'}
      alt="Illustrazione animata delle tre anime di Lempole: radice, ponte e frutto"
      loading="eager"
      decoding="async"
      draggable="false"
      data-testid="triptico-mobile-gif"
    />
  );
}


/**
 * TripticoAnime — animated illustration of the three "souls".
 *
 * The illustration is a short silent hand-drawn video (~9s) rendered onto a
 * <canvas> with a multiply composite against the section's cream background,
 * so the paper of the video disappears and only the ink strokes remain — as
 * if hand-drawn directly on the page.
 *
 * Why canvas instead of `mix-blend-mode: multiply` on the <video>?
 *   iOS Safari renders <video> in a hardware compositor layer that ignores
 *   CSS blend modes. Canvas draws pixels in the same layer as the rest of
 *   the DOM, so the multiply works everywhere.
 */
export default function TripticoAnime({ t }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  // Mobile viewports get a static image instead of the canvas/video setup:
  // multiple mobile browsers can't reliably composite the video → canvas
  // multiply pipeline (Safari data-saver, low-power mode, Chrome mobile
  // battery-throttled). The static image ships all three labels baked in
  // and matches the section's cream background pixel-perfect.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  // Desktop fallback: if the browser blocks muted autoplay (Firefox strict
  // mode, Brave, Chrome battery-saver, some corporate policies) the canvas
  // pipeline would render a blank cream rectangle. We flip to a static
  // WebP illustration after ~1.5s if the video hasn't started playing.
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (isMobile) return; // canvas pipeline skipped on mobile
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!video || !canvas || !wrap) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Resolve the section's cream background from CSS custom properties so
    // the multiply target always matches the surrounding page.
    const cssVar = (name, fallback) =>
      getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
    const cream = cssVar('--lem-ch-souls', cssVar('--lem-bg', '#fbf6ea'));

    const ctx = canvas.getContext('2d', { alpha: false });
    let rafId = null;
    let running = true;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width  = Math.max(1, Math.round(rect.width  * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      canvas.style.width  = rect.width  + 'px';
      canvas.style.height = rect.height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    // Compute the rectangle for `object-fit: contain` behaviour.
    const drawFrame = () => {
      if (!running) return;
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      const vw = video.videoWidth  || cw;
      const vh = video.videoHeight || ch;

      // Fill with the section cream — the multiply target
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = cream;
      ctx.fillRect(0, 0, cw, ch);

      if (video.readyState >= 2 && vw > 0 && vh > 0) {
        const ratio = Math.min(cw / vw, ch / vh);
        const dw = vw * ratio;
        const dh = vh * ratio;
        const dx = (cw - dw) / 2;
        const dy = (ch - dh) / 2;

        // Multiply the video frame onto the cream — paper (near-white)
        // disappears into cream, ink strokes stay dark.
        ctx.globalCompositeOperation = 'multiply';
        try { ctx.drawImage(video, dx, dy, dw, dh); } catch (_) {/* video not ready */}
        ctx.globalCompositeOperation = 'source-over';
      }
    };

    // Prefer requestVideoFrameCallback where available for lock-step redraws.
    let vfcHandle = 0;
    const scheduleVfc = () => {
      if (!running) return;
      vfcHandle = video.requestVideoFrameCallback(() => {
        drawFrame();
        scheduleVfc();
      });
    };
    const loopRaf = () => {
      if (!running) return;
      drawFrame();
      rafId = requestAnimationFrame(loopRaf);
    };

    const startLoop = () => {
      if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) scheduleVfc();
      else loopRaf();
    };
    // Always draw one initial frame (cream) so the canvas isn't blank
    // before the video loads.
    drawFrame();

    if (reduced) {
      // Draw a single frame when the video reaches its first paintable state
      // and stop — no autoplay in reduced-motion.
      video.addEventListener('loadeddata', () => { drawFrame(); }, { once: true });
      return () => {
        running = false;
        ro.disconnect();
      };
    }

    try { video.load(); } catch (_) {}

    // Kick the drawing loop as soon as any pixels are available.
    // Draw once at loadeddata so even a paused video shows its first frame
    // on the canvas (critical when autoplay is blocked by the browser).
    video.addEventListener('loadeddata', drawFrame, { once: true });
    video.addEventListener('play', startLoop, { once: true });

    // Attempt to start playback proactively (before the IO fires, and
    // regardless of viewport position). Some browsers reject this promise
    // silently when autoplay is blocked → the fallback timer below picks
    // it up and swaps in a static image.
    const tryPlay = () => {
      try {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) {}
    };
    tryPlay();

    // IntersectionObserver keeps the animation quiet when off-screen and
    // ensures playback restarts every time the section returns to view.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            tryPlay();
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(wrap);

    // Cross-device gesture unlock: any first interaction (touch, click,
    // key press, scroll, mousemove) re-attempts playback. This covers
    // desktop browsers with strict autoplay policies (Firefox strict,
    // Brave shields, Chrome battery-saver) where muted autoplay may still
    // be blocked.
    const gestureEvents = ['touchstart', 'pointerdown', 'click', 'keydown', 'scroll', 'mousemove'];
    const kick = () => {
      tryPlay();
      // If playback actually starts we can drop the extra listeners; if
      // not, keep them alive so the next gesture retries.
      if (!video.paused) removeGestureListeners();
    };
    const removeGestureListeners = () => {
      gestureEvents.forEach((ev) => {
        try { document.removeEventListener(ev, kick, true); } catch (_) {}
        try { window.removeEventListener(ev, kick, true); } catch (_) {}
      });
    };
    gestureEvents.forEach((ev) => {
      document.addEventListener(ev, kick, { passive: true, capture: true });
      window.addEventListener(ev, kick, { passive: true, capture: true });
    });

    // Fallback timer: if after ~1.5s the video is still paused or hasn't
    // reached HAVE_FUTURE_DATA (readyState < 3), swap the canvas for a
    // static illustration so the user always sees the artwork.
    const fallbackTimer = setTimeout(() => {
      if (!running) return;
      if (video.paused || video.readyState < 3) {
        setShowFallback(true);
      }
    }, 1500);

    // If playback eventually starts (e.g. after a user gesture) we hide
    // the fallback so the animation takes over again.
    const onPlaying = () => setShowFallback(false);
    video.addEventListener('playing', onPlaying);

    return () => {
      running = false;
      clearTimeout(fallbackTimer);
      ro.disconnect();
      io.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      if (vfcHandle && video.cancelVideoFrameCallback) {
        try { video.cancelVideoFrameCallback(vfcHandle); } catch (_) {}
      }
      removeGestureListeners();
      video.removeEventListener('playing', onPlaying);
    };
  }, [isMobile]);

  const items = [
    { key: 'root', title: t.twoSouls.root.title },
    { key: 'bridge', title: t.twoSouls.bridge.title },
    { key: 'pop', title: t.twoSouls.pop.title },
  ];

  return (
    <div className="max-w-6xl mx-auto reveal">
      <div className="tri-illustration">
        <div ref={wrapRef} className="tri-illustration__frame tri-illustration__frame--video">
          {isMobile ? (
            <MobileTrittico />
          ) : (
            <>
              {/* Off-screen decoding source — drawn onto the canvas each frame */}
              <video
                ref={videoRef}
                className="tri-illustration__video-src"
                muted
                playsInline
                webkit-playsinline="true"
                autoPlay
                loop
                preload="auto"
                aria-hidden="true"
              >
                <source src={process.env.PUBLIC_URL + '/illustrations/anima-tree.clean.webm'} type="video/webm" />
                <source src={process.env.PUBLIC_URL + '/illustrations/anima-tree.clean.mp4'} type="video/mp4" />
              </video>

              <canvas
                ref={canvasRef}
                className="tri-illustration__canvas"
                aria-label="Illustrazione animata delle tre anime di Lempole: la radice, il ponte con il vigneron, il frutto"
                role="img"
              />

              {/* Desktop autoplay-blocked fallback: shown only when the
                  browser refuses to autoplay the muted video after 1.5s.
                  Uses the last video frame pre-multiplied against cream
                  so it looks identical to a running video paused at end. */}
              {showFallback && (
                <img
                  className="tri-illustration__fallback"
                  src={process.env.PUBLIC_URL + '/illustrations/anima-tree.desktop.webp'}
                  alt="Illustrazione delle tre anime di Lempole: radice, ponte, frutto"
                  loading="eager"
                  decoding="async"
                  draggable="false"
                  data-testid="triptico-desktop-fallback"
                />
              )}

              <span className="tri-video-mask tri-video-mask--gemini" aria-hidden="true" />
              {!showFallback && (
                <span className="tri-anima-label tri-anima-label--root">anima root — la radice</span>
              )}
            </>
          )}

          {/* Click hotspots — same for both variants */}
          <div className="tri-illustration__hotspots">
            <a href="#soul-pop"    aria-label={items.find((x) => x.key === 'pop').title}    className="tri-hotspot tri-hotspot--pop" />
            <a href="#soul-bridge" aria-label={items.find((x) => x.key === 'bridge').title} className="tri-hotspot tri-hotspot--bridge" />
            <a href="#soul-root"   aria-label={items.find((x) => x.key === 'root').title}   className="tri-hotspot tri-hotspot--root" />
          </div>
        </div>
      </div>

      <div className="mt-10 md:mt-14 flex flex-col items-center text-center trittico-scroll" />
    </div>
  );
}
